import { FactoryProvider, inject } from '@angular/core';
import { provideFilters } from '@shared/filters/filters.service';
import { ActivatedRoute } from '@angular/router';
import { FormControl } from '@angular/forms';
import { arrayOptions, resultOptions } from '@mastechnicaegroup/carelinelive-ui';
import { CommissionerService } from '@lib/commissioner/commissioner.service';
import { toResult } from '@mastechnicaegroup/carelinelive-cdk/rx';
import { map } from 'rxjs';
import { CommissionerName } from '@lib/api/models/commissioner';
import { BooleanFilter, Filter, MultiSelectFilter, RegionFilter } from '@shared/filters/filter';
import { SettingsService } from '@lib/setting/settings.service';
import { CareTypeName, ProviderName } from '@lib/api/models/setting';
import { ApiResponse } from '@lib/api/responses/api-response';
import { ProviderService } from '@lib/provider/provider.service';
import { RegionService } from '@lib/region/region.service';
import { dateRangeValidator } from '@lib/validators/date-range-validator';

export function providePackageFilters(): FactoryProvider {
    return provideFilters(() => {
        const activatedRoute = inject(ActivatedRoute);
        const commissionerService = inject(CommissionerService);
        const settingsService = inject(SettingsService);
        const providerService = inject(ProviderService);
        const regionService = inject(RegionService);

        const commissionerValue: string[] | null = activatedRoute.snapshot.queryParams['commissioner']
            ? [].concat(activatedRoute.snapshot.queryParams['commissioner'].split(','))
            : null;
        const caretypeValue: string[] | null = activatedRoute.snapshot.queryParams['caretype']
            ? [].concat(activatedRoute.snapshot.queryParams['caretype'].split(','))
            : null;
        const providerValue: string[] | null = activatedRoute.snapshot.queryParams['provider']
            ? [].concat(activatedRoute.snapshot.queryParams['provider'].split(','))
            : null;
        const startDateValues: string[] | null = activatedRoute.snapshot.queryParams['startdate']
            ? [].concat(activatedRoute.snapshot.queryParams['startdate'].split(','))
            : [];
        const startDateValue1 = startDateValues.length > 0 ? startDateValues[0] : null;
        const startDateValue2 = startDateValues.length > 1 ? startDateValues[1] : null;

        const dateRangeFormControls = [
            new FormControl<string | null>(startDateValue1),
            new FormControl<string | null>(startDateValue2)
        ];

        dateRangeFormControls.forEach((control, index) => {
            control.setValidators(() => dateRangeValidator(dateRangeFormControls[0], dateRangeFormControls[1], index));
        });

        const regionValues: string[] | null = activatedRoute.snapshot.queryParams['region']
            ? [].concat(activatedRoute.snapshot.queryParams['region'].split(','))
            : [];

        const regionOperators: string[] | null = activatedRoute.snapshot.queryParams['region_o']
            ? [].concat(activatedRoute.snapshot.queryParams['region_o'].split(','))
            : [];

        const regionZonesPrioritiesOptions$ = regionService.getAllRegions();

        return [
            new Filter(
                'startdate',
                'date',
                'Start Date',
                dateRangeFormControls[0],
                new FormControl<string | null>(activatedRoute.snapshot.queryParams['startdate_o'] || 'between'),
                undefined,
                dateRangeFormControls[1]
            ),
            new BooleanFilter<string>(
                'open',
                'Open',
                new FormControl<string | null>(activatedRoute.snapshot.queryParams['open']),
                new FormControl<string | null>(activatedRoute.snapshot.queryParams['open_o'] || 'is'),
                arrayOptions<string>([
                    { label: 'Yes', value: 'true' },
                    { label: 'No', value: 'false' }
                ])
            ),
            new Filter(
                'weeklyhours',
                'number',
                'Weekly Hours',
                new FormControl<number | null>(activatedRoute.snapshot.queryParams['weeklyhours']),
                new FormControl<string | null>(activatedRoute.snapshot.queryParams['weeklyhours_o'])
            ),
            new Filter(
                'carers',
                'number',
                'Number Carers',
                new FormControl<number | null>(activatedRoute.snapshot.queryParams['carers']),
                new FormControl<string | null>(activatedRoute.snapshot.queryParams['carers_o'])
            ),
            new Filter(
                'clients',
                'number',
                'Number Clients',
                new FormControl<number | null>(activatedRoute.snapshot.queryParams['clients']),
                new FormControl<string | null>(activatedRoute.snapshot.queryParams['clients_o'])
            ),
            new Filter(
                'town',
                'text',
                'Town',
                new FormControl<string | null>(activatedRoute.snapshot.queryParams['town']),
                new FormControl<string | null>(activatedRoute.snapshot.queryParams['town_o'])
            ),
            new Filter(
                'county',
                'text',
                'County',
                new FormControl<string | null>(activatedRoute.snapshot.queryParams['county']),
                new FormControl<string | null>(activatedRoute.snapshot.queryParams['county_o'])
            ),
            new Filter(
                'country',
                'text',
                'Country',
                new FormControl<string | null>(activatedRoute.snapshot.queryParams['country']),
                new FormControl<string | null>(activatedRoute.snapshot.queryParams['country_o'])
            ),
            new BooleanFilter<string>(
                'hardtoreach',
                'Hard to Reach',
                new FormControl<string | null>(activatedRoute.snapshot.queryParams['hardtoreach']),
                new FormControl<string | null>(activatedRoute.snapshot.queryParams['hardtoreach_o'] || 'is'),
                arrayOptions<string>([
                    { label: 'Yes', value: 'true' },
                    { label: 'No', value: 'false' }
                ])
            ),
            new MultiSelectFilter<string>(
                'commissioner',
                'Commissioner',
                new FormControl<string[] | null>(commissionerValue),
                new FormControl<string | null>(activatedRoute.snapshot.queryParams['commissioner_o']),
                resultOptions(commissionerService.getCommissionerNames().pipe(
                    map((response: ApiResponse<CommissionerName[]>) => response.data.map(item => {
                        return {
                            label: item.name + ' ' + item.reference,
                            value: item.id
                        };
                    })),
                    toResult()
                ), {
                    multi: {
                        showSelectAllButton: true,
                        showOnlyButton: true
                    },
                    searchable: true
                })
            ),
            new MultiSelectFilter<string>(
                'caretype',
                'Care Type',
                new FormControl<string[] | null>(caretypeValue),
                new FormControl<string | null>(activatedRoute.snapshot.queryParams['caretype_o']),
                resultOptions(settingsService.getAllPossibleCareTypes().pipe(
                    map((response: ApiResponse<CareTypeName[]>) => response.data.map(item => {
                        return {
                            label: item.name,
                            value: item.id
                        };
                    })),
                    toResult()
                ), {
                    multi: {
                        showSelectAllButton: true,
                        showOnlyButton: true
                    },
                    searchable: true
                })
            ),
            new MultiSelectFilter<string>(
                'provider',
                'Provider',
                new FormControl<string[] | null>(providerValue),
                new FormControl<string | null>(activatedRoute.snapshot.queryParams['provider_o']),
                resultOptions(providerService.getProviderNames().pipe(
                    map((response: ApiResponse<ProviderName[]>) => response.data.map(item => {
                        return {
                            label: item.name + ' ' + item.reference,
                            value: item.id
                        };
                    })),
                    toResult()
                ), {
                    multi: {
                        showSelectAllButton: true,
                        showOnlyButton: true
                    },
                    searchable: true
                })
            ),
            new RegionFilter(
                'region',
                regionZonesPrioritiesOptions$,
                'Region',
                new FormControl<string | null>(regionValues[0] ? regionValues[0] : null),
                new FormControl<string | null>(regionOperators[0] ? regionOperators[0] : null),
                'Zone',
                new FormControl<string | null>(regionValues[1] ? regionValues[1] : null),
                new FormControl<string | null>(regionOperators[1] ? regionOperators[1] : null),
                'Priority',
                new FormControl<string | null>(regionValues[2] ? regionValues[2] : null),
                new FormControl<string | null>(regionOperators[2] ? regionOperators[2] : null)
            ),
            new BooleanFilter<string>(
                'rejected',
                'Rejected',
                new FormControl<string | null>(activatedRoute.snapshot.queryParams['rejected']),
                new FormControl<string | null>(activatedRoute.snapshot.queryParams['rejected_o'] || 'is'),
                arrayOptions<string>([
                    { label: 'Yes', value: 'true' },
                    { label: 'No', value: 'false' }
                ])
            ),
            new BooleanFilter<string>(
                'offered',
                'Offered',
                new FormControl<string | null>(activatedRoute.snapshot.queryParams['offered']),
                new FormControl<string | null>(activatedRoute.snapshot.queryParams['offered_o'] || 'is'),
                arrayOptions<string>([
                    { label: 'Yes', value: 'true' },
                    { label: 'No', value: 'false' }
                ])
            ),
            new BooleanFilter<string>(
                'requested',
                'Requested',
                new FormControl<string | null>(activatedRoute.snapshot.queryParams['requested']),
                new FormControl<string | null>(activatedRoute.snapshot.queryParams['requested_o'] || 'is'),
                arrayOptions<string>([
                    { label: 'Yes', value: 'true' },
                    { label: 'No', value: 'false' }
                ])
            ),
            new BooleanFilter<string>(
                'notInterested',
                'Not Interested',
                new FormControl<string | null>(activatedRoute.snapshot.queryParams['notInterested']),
                new FormControl<string | null>(activatedRoute.snapshot.queryParams['notInterested_o'] || 'is'),
                arrayOptions<string>([
                    { label: 'Yes', value: 'true' },
                    { label: 'No', value: 'false' }
                ])
            ),
        ];
    });
}
